// import { format } from 'date-fns'; // Available for future date formatting
// import { MarketplaceCompany } from '../data/marketplace-schemas'; // Type available if needed
import { marketplaceCompanies } from '../data/company-profiles';
import { marketAnalysis } from '../data/market-analysis';
import type { FilterOptions, BubbleChartDataPoint, LineChartDataPoint, BarChartDataPoint, ExportConfig } from '../types/visualization';

// Company color mapping for consistent visualization
export const COMPANY_COLORS = {
  thumbtack: '#1a73e8',
  taskrabbit: '#00d084', 
  angi: '#ff6900',
  handy: '#6b46c1',
  porch_group: '#ef4444',
} as const;

// Utility function to get company color
export const getCompanyColor = (companyId: string): string => {
  return COMPANY_COLORS[companyId as keyof typeof COMPANY_COLORS] || '#64748b';
};

// Format currency values
export const formatCurrency = (value: number, unit: 'millions' | 'billions' = 'millions'): string => {
  const multiplier = unit === 'billions' ? 1000 : 1;
  const suffix = unit === 'billions' ? 'B' : 'M';
  return `$${(value / multiplier).toFixed(1)}${suffix}`;
};

// Format percentage values
export const formatPercentage = (value: number): string => {
  return `${value.toFixed(1)}%`;
};

// Format large numbers
export const formatNumber = (value: number): string => {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return value.toString();
};

// Generate competitive positioning data for bubble chart
export const generateCompetitivePositioningData = (filters: FilterOptions = {}): BubbleChartDataPoint[] => {
  return marketplaceCompanies
    .filter(company => !filters.company || company.id === filters.company)
    .filter(company => company.status !== 'defunct')
    .map(company => {
      const revenue = company.financial_metrics.revenue.amount;
      const valuation = company.financial_metrics.valuation?.amount || 0;
      const gmv = company.financial_metrics.gmv?.amount || revenue * 3; // Estimate if not available
      const marketShare = marketAnalysis.segment_analysis
        .filter(segment => company.primary_segments.includes(segment.segment))
        .reduce((acc, segment) => acc + segment.market_share, 0) / company.primary_segments.length;

      return {
        x: revenue,
        y: valuation,
        z: gmv,
        companyId: company.id,
        companyName: company.name,
        color: getCompanyColor(company.id),
        additionalData: {
          marketShare,
          status: company.status,
          segments: company.primary_segments,
        },
      };
    });
};

// Generate revenue growth data for line chart
export const generateRevenueGrowthData = (filters: FilterOptions = {}): LineChartDataPoint[] => {
  const data: LineChartDataPoint[] = [];
  
  marketplaceCompanies
    .filter(company => !filters.company || company.id === filters.company)
    .filter(company => company.status !== 'defunct')
    .forEach(company => {
      const currentRevenue = company.financial_metrics.revenue.amount;
      const currentYear = company.financial_metrics.revenue.year;
      
      // Historical data (estimated based on growth patterns)
      for (let year = 2020; year <= 2025; year++) {
        let revenue = currentRevenue;
        
        if (year < currentYear) {
          // Estimate historical data with typical growth rates
          const yearsBack = currentYear - year;
          const growthRate = company.id === 'thumbtack' ? 0.25 : 
                           company.id === 'angi' ? 0.15 :
                           company.id === 'taskrabbit' ? 0.20 :
                           company.id === 'porch_group' ? 0.30 :
                           0.18;
          revenue = currentRevenue / Math.pow(1 + growthRate, yearsBack);
        } else if (year > currentYear) {
          // Project future data
          const yearsAhead = year - currentYear;
          const projectedGrowthRate = company.id === 'thumbtack' ? 0.22 :
                                    company.id === 'angi' ? 0.12 :
                                    company.id === 'taskrabbit' ? 0.18 :
                                    company.id === 'porch_group' ? 0.25 :
                                    0.15;
          revenue = currentRevenue * Math.pow(1 + projectedGrowthRate, yearsAhead);
        }
        
        data.push({
          date: year.toString(),
          value: revenue,
          companyId: company.id,
          companyName: company.name,
          color: getCompanyColor(company.id),
        });
      }
    });
    
  return data.sort((a, b) => a.date.localeCompare(b.date));
};

// Generate take rate comparison data
export const generateTakeRateData = (filters: FilterOptions = {}): BarChartDataPoint[] => {
  return marketplaceCompanies
    .filter(company => !filters.company || company.id === filters.company)
    .filter(company => company.status !== 'defunct')
    .map(company => {
      // Estimate take rates based on business model
      const takeRate = company.financial_metrics.take_rate?.percentage ||
        (company.business_model === 'commission_based' ? 15 :
         company.business_model === 'lead_generation' ? 25 :
         company.business_model === 'transaction_fee' ? 3 :
         company.business_model === 'hybrid' ? 12 : 10);
      
      return {
        category: company.name,
        value: takeRate,
        companyId: company.id,
        companyName: company.name,
        color: getCompanyColor(company.id),
      };
    });
};

// Generate category breakdown data
export const generateCategoryBreakdownData = (filters: FilterOptions = {}): BarChartDataPoint[] => {
  const segmentData = marketAnalysis.segment_analysis
    .filter(segment => !filters.segment || segment.segment === filters.segment)
    .map(segment => ({
      category: segment.segment.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      value: segment.market_share,
      companyId: 'market',
      companyName: 'Market Share',
      color: `hsl(${hashString(segment.segment) % 360}, 70%, 50%)`,
    }));
  
  return segmentData;
};

// Generate geographic coverage data
export const generateGeographicData = () => {
  return marketplaceCompanies
    .filter(company => company.status !== 'defunct')
    .map(company => ({
      companyId: company.id,
      companyName: company.name,
      coverage: {
        ...company.geographic_coverage,
        total_cities: company.geographic_coverage.total_cities || 0,
        total_states: company.geographic_coverage.total_states || 0,
      },
      color: getCompanyColor(company.id),
    }));
};

// Export chart as image or data
export const exportChart = async (config: ExportConfig, chartElement: HTMLElement, data?: unknown[]): Promise<void> => {
  if (config.format === 'csv' && data) {
    exportToCSV(data, config.filename);
    return;
  }
  
  // For image exports, we would use html2canvas or similar library
  console.log('Export functionality would be implemented here', { config, chartElement });
};

// Export data to CSV
const exportToCSV = (data: unknown[], filename: string): void => {
  if (data.length === 0) return;
  
  const headers = Object.keys(data[0] as Record<string, unknown>);
  const csvContent = [
    headers.join(','),
    ...data.map(row => 
      headers.map(header => 
        JSON.stringify((row as Record<string, unknown>)[header])
      ).join(',')
    )
  ].join('\n');
  
  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Hash function for generating colors
const hashString = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
};